<template>
  <Card class="card p-5">
    <div class="row">
      <Input
          containerClass="col-6"
          name="project-type-name"
          type="text"
          :label="$t('projectTypes.label.name')"
          labelClass="form-label fs-6 text-black"
          :inputClass="'form-control form-control-sm form-control-solid'"
          :isRequired="true"
          size="lg"
          :placeholder="$t('projectTypes.placeholder.name')"
          v-model="formData.name"/>

      <Input
          containerClass="col-2"
          name="project-type-code"
          type="text"
          :label="$t('projectTypes.label.code')"
          labelClass="form-label fs-6 text-black"
          :inputClass="'form-control form-control-sm form-control-solid'"
          size="lg"
          :isRequired="false"
          :placeholder="$t('projectTypes.placeholder.code')"
          v-model="formData.prefix"/>

      <Select2
          container-class="form-group col-3 "
          :multiple="false"
          :show-search="false"
          label-class="form-label fs-6 text-black"
          :label="$t('projectTypes.label.categoryGroup')"
          :selectClass="'form-select form-control-sm form-select-solid rounded-start-0'"
          :name="`project_category_group_id`"
          :is-required="false"
          v-model="formData.project_category_group_id">
        <option v-for="(group, index) in categoryGroups" :value="group.id" :key="index">
          {{ group?.name }}
        </option>
      </Select2>

      <div class="col-1">
        <label class="form-label fs-6 text-black">
          {{$t('projectTypes.label.color')}}
        </label>
        <div >
          <ColorPicker
              v-model:pureColor="formData.color"
              format="hex"
          />
        </div>
      </div>
    </div>
    <div class="row mt-4">
      <div class="form-group">
        <label class="form-label fs-6 text-black">{{ $t('projectTypes.label.description') }}</label>
        <textarea v-model="formData.description" class="form-control form-control-solid" rows="3"></textarea>
      </div>
    </div>

  </Card>
  <br>
  <div class="d-flex flex-row-reverse">
    <button @click.prevent="addStepField" class="btn btn-flex btn-primary" >
          <span class="svg-icon svg-icon-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect opaicon="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
              <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
            </svg>
          </span>
      {{$t('projectTypes.addStep')}}
    </button>
  </div>
  <br>

  <Card class="card my-5 p-5">
    <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="step-datatable" style="margin-top: 13px !important; cursor:pointer">
      <thead>
      <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
        <th class="">{{ $t('projectTypes.table.step')}}</th>
        <th class="">{{ $t('projectTypes.table.delay')}}</th>
        <th class="">{{ $t('projectTypes.table.progressPercent')}}</th>
        <th class="">{{ $t('projectTypes.table.statusCount')}}</th>
        <th class="">{{ $t('projectTypes.table.taskCount')}}</th>
        <th class="">{{ $t('tasks.label.beReview')}}</th>
        <th class="w-200px">{{ $t('actions') }}</th>
      </tr>
      </thead>
      <Sortable id="stepSortable" @change="handleChangeOrder" :processing="isOrdering">
        <ProjectTypeFormConfig
            v-if="formData.configs && formData.configs.length"
            v-for="(config, index) in formData.configs"
            :key="config.milestone_category_id"
            :index="index"
            :config="config"
            :selectedSteps="selectedStepsIds"
            @delete-config="removeConfigRow"
        />
      </Sortable>

    </table>
  </Card>

  <StepSelectionForm id="selection_step_create" :form="newStepFormData" @save="handleStepSave" :selectedSteps="selectedStepsIds" />

  <template v-if="$canAccess(moduleAbilities.NOTARIAL_FORMALITIES)">
   <div  v-if="$can(abilities.VIEW_FORMALITIES_PARAMETERS) || $can(abilities.EDIT_FORMALITIES_PARAMETERS)">
     <div class="separator pt-5 mb-5"></div>
     <h1 class=""></h1>
     <h3 class="mb-5 align-items-start flex-column">
       <span class="fw-bold text-gray-900 fs-2x">Formalités</span>
       <br>
       <span class="text-gray-500 mt-1 fw-semibold fs-6">Ajouter les tableau de formalités</span>
     </h3>
     <formalities-tables-builder v-model="formData.tables" />
   </div>
  </template>

</template>

<script setup>
import {computed, defineProps, onMounted, ref, watch} from 'vue'
import Input from "@/components/Input.vue";
import { useDiscountType } from "@/composables/discountType";
import {useStore} from "vuex";
import Card from "@/Modules/CRM/components/Card.vue";
import {useRoute, useRouter} from 'vue-router';
import {useLocales} from "@/composables/locales";
import { ColorPicker } from "vue3-colorpicker";
import "vue3-colorpicker/style.css";
import Sortable from "@/components/Sortable.vue";
import Select2 from '@/components/Select2.vue';
import ProjectTypeFormConfig from "@/Modules/Projects/components/ProjectType/ProjectTypeFormConfig.vue";
import StepSelectionForm from "@/Modules/Projects/components/ProjectType/StepSelectionForm.vue";
import Button from "@/components/Button.vue";
import FormalitiesTablesBuilder from "@/Modules/Projects/components/FormalitiesTablesBuilder.vue";
import moduleAbilities from "@/composables/moduleAbilities";
import abilities from "@/composables/abilities";


const props = defineProps({
  type: { type: Object, default: () => ({
      id: null,
      name: "",
      description: "",
      prefix: "",
      configs: [],
      tables: []
    }) },
})

const tables = computed(() => formData.tables || [])

const isOrdering = ref(false)
const formData = ref(props.type)
const newStepFormData = ref({
  milestone_category_id: '',
  milestone_category_name: '',
  delay_value: '',
  delay_unit: '',
  milestone_categories_progress: '',
  status: [],
  tasks: [],
})

const {discountType} = useDiscountType()
const {locale} = useLocales()
const route = useRoute()

const store = useStore()
const router = useRouter()

const activeStep = ref(0)

const tasks = ref([null])

const milestones = computed(() => store.getters['milestoneCategories/getTaskMilestones'])

const taskStatus = computed(() => store.getters['taskStatus/getTaskStatus'])
const categoryGroups = computed(() => store.getters['projectCategories/getProjectCategoryGroups'])
const frequencyEnum = computed(() => store.getters["configs/getFrequencyEnum"])
const selectedStepsIds = computed(() => formData.value.configs.map(el => el.milestone_category_id) || [])


function addStepField(){
  activeStep.value = formData.value?.configs?.length
  $('#selection_step_create').modal('toggle')
}
function removeConfigRow(index){
  if (index <= formData.value?.configs?.length) {
    activeStep.value = 0
    formData.value.configs.splice(index, 1)
  }
}
function handleStepSave(data) {
  formData.value.configs.push(data)
  newStepFormData.value = null
  newStepFormData.value = {
    milestone_category_id: '',
    milestone_category_name: '',
    delay_value: '',
    delay_unit: '',
    milestone_categories_progress: '',
    status: [],
    tasks: [],
  }
}

function handleChangeOrder({ oldIndex, newIndex }) {
  try {
    isOrdering.value = true
    const configs = formData.value.configs;
    if (
        oldIndex === newIndex ||
        oldIndex < 0 || oldIndex >= configs.length ||
        newIndex < 0 || newIndex > configs.length
    ) return;

    const itemToMove = configs[oldIndex];
    configs.splice(oldIndex, 1);
    configs.splice(newIndex, 0, itemToMove);
    formData.value.configs = configs
  } finally {
    isOrdering.value = false
  }
}

onMounted(() => {
  store.dispatch('configs/loadFrequencyEnum')
})
</script>